import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, X, ArrowRight, Copy, Check, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import { api, formatApiError } from "@/lib/api";

const EMPTY = {
  make: "",
  model: "",
  year: "",
  max_mileage: "",
  exterior_color: "",
  interior_color: "",
  budget: "",
  title_type: "Clean",
  desired_features: "",
  delivery_zip: "",
  additional_notes: "",
  customer_name: "",
  customer_email: "",
  customer_phone: "",
};

const FindMyCar = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState([]); // {path, url, name}
  const [agreed, setAgreed] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [copied, setCopied] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((prev) => [...prev, { path: data.path, url: data.url, name: file.name }]);
      }
      toast.success(`${files.length} photo(s) uploaded`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = (idx) => {
    setPhotos((p) => p.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Basic required-field validation
    const required = ["make", "model", "year", "delivery_zip", "customer_name", "customer_email"];
    for (const r of required) {
      if (!form[r]) {
        toast.error(`Please fill: ${r.replace("_", " ")}`);
        return;
      }
    }
    if (!agreed) {
      toast.error("Please agree to the Policies before submitting");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        year: parseInt(form.year, 10),
        max_mileage: form.max_mileage ? parseInt(form.max_mileage, 10) : null,
        budget: form.budget ? parseFloat(form.budget) : null,
        photo_paths: photos.map((p) => p.path),
      };
      const { data } = await api.post("/requests", payload);
      setSuccess(data);
      toast.success("Request submitted!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const copyId = () => {
    if (!success?.request_id) return;
    navigator.clipboard.writeText(success.request_id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] text-slate-100">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
          <div
            data-testid="submission-success-card"
            className="bg-gradient-to-b from-[#18181E] to-[#121216] border border-amber-500/30 rounded-3xl p-8 sm:p-12 text-center shadow-[0_0_60px_rgba(245,158,11,0.1)]"
          >
            <div className="w-16 h-16 rounded-full bg-amber-500 mx-auto flex items-center justify-center">
              <Check className="w-8 h-8 text-black" strokeWidth={3} />
            </div>
            <div className="mt-6 text-xs uppercase tracking-[0.3em] font-semibold text-amber-400">
              / Request Submitted
            </div>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl font-extrabold uppercase tracking-tight">
              You&apos;re on the <span className="gold-text">road</span>
            </h2>
            <p className="mt-4 text-slate-400">
              We&apos;ve received your request. Our team will begin sourcing immediately.
              Save your Request ID below.
            </p>

            <div className="mt-8 p-6 bg-[#0A0A0C] border border-white/10 rounded-2xl">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">
                Your Request ID
              </div>
              <div className="mt-2 flex items-center justify-center gap-3">
                <span
                  data-testid="success-request-id"
                  className="font-mono-brand text-2xl sm:text-3xl font-bold text-amber-400"
                >
                  {success.request_id}
                </span>
                <button
                  onClick={copyId}
                  data-testid="copy-request-id-btn"
                  className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <Copy className="w-4 h-4 text-slate-400" />
                  )}
                </button>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate(`/track/${success.request_id}`)}
                data-testid="go-to-tracking-btn"
                className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-black font-extrabold uppercase tracking-wider px-7 py-3.5 rounded-xl shadow-lg shadow-amber-500/20 transition"
              >
                Track Request <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setSuccess(null);
                  setForm(EMPTY);
                  setPhotos([]);
                }}
                data-testid="submit-another-btn"
                className="inline-flex items-center justify-center bg-white/5 hover:bg-white/10 text-white font-semibold uppercase tracking-wider px-7 py-3.5 rounded-xl border border-white/15 transition"
              >
                Submit Another
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const inputCls =
    "w-full bg-[#121216] border border-white/10 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 text-slate-100 placeholder:text-slate-600 rounded-xl px-4 py-3 text-sm transition outline-none";
  const labelCls = "text-xs uppercase tracking-[0.15em] font-semibold text-slate-400 mb-2 block";

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-slate-100">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-amber-400">
            / Step 1 of 1
          </div>
          <h1 className="font-display mt-3 text-4xl sm:text-5xl font-extrabold uppercase tracking-tight">
            Find my <span className="gold-text">car</span>
          </h1>
          <p className="mt-4 text-slate-400 max-w-2xl">
            Tell us exactly what you want. The more specific, the better the match.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="find-my-car-form"
          className="bg-gradient-to-b from-[#18181E] to-[#121216] border border-white/10 rounded-3xl p-6 sm:p-10 space-y-10"
        >
          {/* Vehicle Section */}
          <div>
            <h3 className="font-display text-lg font-bold uppercase tracking-wider text-amber-400 mb-6">
              Vehicle
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <div>
                <label className={labelCls}>Make *</label>
                <input
                  data-testid="input-make"
                  className={inputCls}
                  value={form.make}
                  onChange={(e) => set("make", e.target.value)}
                  placeholder="Toyota"
                />
              </div>
              <div>
                <label className={labelCls}>Model *</label>
                <input
                  data-testid="input-model"
                  className={inputCls}
                  value={form.model}
                  onChange={(e) => set("model", e.target.value)}
                  placeholder="Camry"
                />
              </div>
              <div>
                <label className={labelCls}>Year *</label>
                <input
                  data-testid="input-year"
                  type="number"
                  className={inputCls}
                  value={form.year}
                  onChange={(e) => set("year", e.target.value)}
                  placeholder="2022"
                />
              </div>
              <div>
                <label className={labelCls}>Max Mileage</label>
                <input
                  data-testid="input-max-mileage"
                  type="number"
                  className={inputCls}
                  value={form.max_mileage}
                  onChange={(e) => set("max_mileage", e.target.value)}
                  placeholder="60000"
                />
              </div>
              <div>
                <label className={labelCls}>Exterior Color</label>
                <input
                  data-testid="input-exterior-color"
                  className={inputCls}
                  value={form.exterior_color}
                  onChange={(e) => set("exterior_color", e.target.value)}
                  placeholder="Pearl White"
                />
              </div>
              <div>
                <label className={labelCls}>Interior Color</label>
                <input
                  data-testid="input-interior-color"
                  className={inputCls}
                  value={form.interior_color}
                  onChange={(e) => set("interior_color", e.target.value)}
                  placeholder="Black Leather"
                />
              </div>
              <div>
                <label className={labelCls}>Budget (USD)</label>
                <input
                  data-testid="input-budget"
                  type="number"
                  className={inputCls}
                  value={form.budget}
                  onChange={(e) => set("budget", e.target.value)}
                  placeholder="25000"
                />
              </div>
              <div>
                <label className={labelCls}>Title</label>
                <select
                  data-testid="input-title-type"
                  className={inputCls}
                  value={form.title_type}
                  onChange={(e) => set("title_type", e.target.value)}
                >
                  <option value="Clean">Clean Title</option>
                  <option value="Salvage">Salvage Title</option>
                  <option value="Any">Any</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Delivery ZIP *</label>
                <input
                  data-testid="input-delivery-zip"
                  className={inputCls}
                  value={form.delivery_zip}
                  onChange={(e) => set("delivery_zip", e.target.value)}
                  placeholder="94103"
                />
              </div>
            </div>

            <div className="mt-5">
              <label className={labelCls}>Desired Features</label>
              <textarea
                data-testid="input-desired-features"
                className={`${inputCls} min-h-[90px]`}
                value={form.desired_features}
                onChange={(e) => set("desired_features", e.target.value)}
                placeholder="Sunroof, heated seats, apple carplay, backup camera..."
              />
            </div>

            <div className="mt-5">
              <label className={labelCls}>Additional Notes</label>
              <textarea
                data-testid="input-additional-notes"
                className={`${inputCls} min-h-[90px]`}
                value={form.additional_notes}
                onChange={(e) => set("additional_notes", e.target.value)}
                placeholder="Anything else we should know?"
              />
            </div>
          </div>

          {/* Reference Photos */}
          <div>
            <h3 className="font-display text-lg font-bold uppercase tracking-wider text-amber-400 mb-6">
              Reference Photos <span className="text-slate-500 text-xs normal-case tracking-normal">(optional)</span>
            </h3>
            <label
              className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-white/15 hover:border-amber-500/50 bg-[#121216]/50 rounded-2xl p-8 cursor-pointer transition hover:bg-[#18181E]"
              data-testid="photo-upload-dropzone"
            >
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleUpload}
                data-testid="photo-upload-input"
              />
              {uploading ? (
                <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
              ) : (
                <Upload className="w-6 h-6 text-amber-400" />
              )}
              <div className="text-sm text-slate-300 font-semibold">
                {uploading ? "Uploading..." : "Click to upload reference photos"}
              </div>
              <div className="text-xs text-slate-500">PNG, JPG up to 10MB each</div>
            </label>

            {photos.length > 0 && (
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {photos.map((p, i) => (
                  <div
                    key={p.path}
                    className="relative aspect-square rounded-xl overflow-hidden border border-white/10 bg-[#121216]"
                    data-testid={`photo-preview-${i}`}
                  >
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`}
                      alt={p.name}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(i)}
                      data-testid={`remove-photo-${i}`}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 hover:bg-red-500 text-white flex items-center justify-center transition"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-display text-lg font-bold uppercase tracking-wider text-amber-400 mb-6">
              Your Contact
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div>
                <label className={labelCls}>Full Name *</label>
                <input
                  data-testid="input-customer-name"
                  className={inputCls}
                  value={form.customer_name}
                  onChange={(e) => set("customer_name", e.target.value)}
                  placeholder="Jane Driver"
                />
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <input
                  data-testid="input-customer-email"
                  type="email"
                  className={inputCls}
                  value={form.customer_email}
                  onChange={(e) => set("customer_email", e.target.value)}
                  placeholder="jane@example.com"
                />
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input
                  data-testid="input-customer-phone"
                  className={inputCls}
                  value={form.customer_phone}
                  onChange={(e) => set("customer_phone", e.target.value)}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-white/5 space-y-5">
            <label
              className="flex items-start gap-3 cursor-pointer group"
              data-testid="agree-policies-label"
            >
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                data-testid="agree-policies-checkbox"
                className="mt-1 w-5 h-5 rounded border-2 border-white/20 bg-[#121216] accent-amber-500 cursor-pointer focus:ring-2 focus:ring-amber-500"
              />
              <span className="text-sm text-slate-300 group-hover:text-white transition leading-relaxed">
                I agree to the{" "}
                <a
                  href="/policies"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="agree-policies-link"
                  className="text-amber-400 hover:text-amber-300 underline underline-offset-2"
                >
                  Customer Policies & Terms of Service
                </a>{" "}
                and consent to be contacted about this vehicle request.
              </span>
            </label>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs text-slate-500 max-w-md">
                No charges until we find a match. You can decline the found vehicle at any time.
              </p>
              <button
                type="submit"
                disabled={submitting || !agreed}
                data-testid="submit-request-btn"
                className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed text-black font-extrabold uppercase tracking-wider px-8 py-4 rounded-xl shadow-lg shadow-amber-500/20 transition active:scale-[0.98] w-full sm:w-auto"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Submitting...
                  </>
                ) : (
                  <>
                    Submit Request <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FindMyCar;
